import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { delayedTrackZustand } from '@fabric-app/compat/capabilities/openreplay';

import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { SIDEBAR_OPTIONS } from '../components/DashboardSidebarV2/constants';

import type { ZustandDynamicCaller } from '../types/store';
import type { ExtraPanelTab } from '@fabric-app/types/resource-detail/extraPanel';
import type { ApiUserMe } from '@fabric-app/types/user';
import { logTrackerEvent, TrackerEventKind } from '../lib/or';
import type { NewResourceType } from '@fabric-app/business/resources/components/NewResource/config';
import type { StoredDataTransfer } from '@fabric-app/utils/clipboard';
import { omit, pick } from '@fabric-app/utils/object/manipulation';
import type { ResourceFilterType, OfViewMode, ResourceParent } from '@fabric-app/types/resources';
import { SkinTones } from 'emoji-picker-react';

export type NewModalOptions = {
  type?: NewResourceType;

  // instead of specifying a type and it's needed properties we will make all other properties optional,
  // this way if the user is deciding what type of fdoc to create it won't clear the other properties for
  // the other types of fdocs
  url?: string;
  comment?: string;
  shared?: boolean;
  urlTitle?: string;

  text?: string;
  dataTransfer?: StoredDataTransfer;

  fullScreen?: boolean;
  /**
   * set initial parent for the new resource
   * currently supported for folder only
   *
   * if not provided, this value will be deducted from the route
   */
  parent?: ResourceParent;
};

export type SpaceOverviewViewMode = 'Grid' | 'List';

interface FloatingFooterItem {
  key: string;
  priority: number;
}

interface UIStore {
  userLastFetch: number;

  requiredUpdateModalOpen: boolean;
  setRequiredUpdateModalOpen: (open: boolean) => void;

  mobileKeyboardHeight: number;
  setMobileKeyboardHeight: (height: number) => void;
  keyboardOpen: boolean;
  setKeyboardOpen: (open: boolean) => void;
  mobileKeyboardHeightSupported: boolean;
  setMobileKeyboardHeightSupported: (open: boolean) => void;

  supportsPassiveEvents: boolean;

  searchQuery: string;
  filter: ResourceFilterType;
  filterRoute: string;
  spaceOverviewViewMode: SpaceOverviewViewMode;
  zoomLevel: number;
  sidebarOpen: boolean;
  sidebarExpanded: boolean;
  sidebarPreferredWidth: number;
  isNewModalOpen: boolean;
  newModalOptions: NewModalOptions;
  expandedFdocSelector: string | null;
  isExperimentSearchOverlayOpen: boolean;

  lastFdocUpdate: number | null;

  // WEB/DESKTOP ONLY
  expandedFdocSidebarOpen: boolean;
  // Init tab serves to know if the UI should animate stuff in, since if the tab was init
  // we assume there was already a visualization active and re-animating is non-sensical
  expandedFdocSidebarInitTab?: ExtraPanelTab;
  expandedFdocSidebarTab: ExtraPanelTab;
  setExpandedFdocSidebarOpen: (open: boolean) => void;
  setExpandedFdocSidebarTab: (tab: ExtraPanelTab) => void;

  setSupportsPassiveEvents: (supports: boolean) => void;

  setUserLastFetch: (timestamp: number) => void;
  setSearchQuery: (query: string) => void;
  setFilter: (filter: ResourceFilterType) => void;
  setFilterRoute: (filter: string) => void;
  setSpaceOverviewViewMode: (mode: SpaceOverviewViewMode) => void;
  setZoomLevel: (level: number) => void;
  setSidebarOpen: (open: boolean) => void;
  // WEB/DESKTOP ONLY
  setSidebarExpanded: (open: boolean) => void;
  setSidebarPreferredWidth: (newValue: number) => void;
  // WEB/DESKTOP ONLY
  setNewModalOpen: (open: boolean, newModalOptions?: NewModalOptions) => void;
  setNewModalOptions: ZustandDynamicCaller<NewModalOptions>;
  setLastFdocUpdate: (timestamp: number) => void;
  setIsExperimentSearchOverlayOpen: (newState: boolean) => void;
  setExpandedFdocSelector: (selector: string | null) => void;

  reset: () => void;

  /**
   * The skin tone selected in the emoji picker
   */
  emojiPickerSkinTone: SkinTones;
  setEmojiPickerSkinTone: (skinTone: SkinTones) => void;

  /**
   * State to track which floating footer is active
   *
   * If no active key is set, default DashboardFooter will be rendered
   * Active key is the key one of the floatingFooters with the highest priority
   */
  floatingFooterActive: FloatingFooterItem['key'] | null;
  floatingFooters: Array<FloatingFooterItem>;
  /**
   *
   * @param footer
   * @param add if false, footer will be removed
   * @returns
   */
  setFloatingFooter: (footer: FloatingFooterItem, add: boolean) => void;
}

const emptyNewModalOptions: NewModalOptions = {};

const useUIStore = create<UIStore>()(
  delayedTrackZustand(
    persist(
      (set) => ({
        requiredUpdateModalOpen: false,
        setRequiredUpdateModalOpen: (open: boolean) => set({ requiredUpdateModalOpen: open }),
        floatingFooterActive: null,
        floatingFooters: [],
        setFloatingFooter: (footer, add) => {
          set((state) => {
            // remove item to make sure we have unique keys if it's an add operation
            const footersClean = state.floatingFooters.filter((f) => f.key !== footer.key);
            // add item
            const footers = add ? [...footersClean, footer] : footersClean;
            // find highest priority footer
            const active = footers.sort((a, b) => b.priority - a.priority)[0];
            return {
              floatingFooterActive: active?.key || null,
              floatingFooters: footers,
            };
          });
        },

        user: null as ApiUserMe | null,
        userLastFetch: 0,
        searchQuery: '',
        filter: 'All' as ResourceFilterType,
        filterRoute: '/saved',
        viewMode: 'Grid' as OfViewMode,
        viewModeInSpace: 'Grid' as OfViewMode,
        spaceOverviewViewMode: 'Grid' as SpaceOverviewViewMode,
        zoomLevel: 0.9,
        sidebarOpen: false,
        sidebarExpanded: false,
        sidebarPreferredWidth: Number(SIDEBAR_OPTIONS.EXPANDED_WIDTH.RECOMMENDED),
        isNewModalOpen: false,
        newModalOptions: emptyNewModalOptions,
        playableTimestamp: null,
        pdfPage: null,
        mobileKeyboardHeight: 0,
        keyboardOpen: false,
        supportsPassiveEvents: false,
        expandedFdocSelector: null,

        expandedFdocSidebarInitTab: 'assistant',
        expandedFdocSidebarTab: 'assistant',
        expandedFdocSidebarOpen: true,
        chatAssistantOpen: false,
        chatAssistantFullscreen: false,
        isExperimentSearchOverlayOpen: false,
        mobileKeyboardHeightSupported: false,

        lastFdocUpdate: null,

        setSupportsPassiveEvents: (supports: boolean) => set({ supportsPassiveEvents: supports }),

        setUserLastFetch: (timestamp) => set({ userLastFetch: timestamp }),
        setSearchQuery: (query: string) => set({ searchQuery: query }),
        setFilter: (filter: ResourceFilterType) => set({ filter }),
        setFilterRoute: (filter: string) => set({ filterRoute: filter }),
        setSpaceOverviewViewMode: (mode: SpaceOverviewViewMode) =>
          set({ spaceOverviewViewMode: mode }),
        setZoomLevel: (level: number) => set({ zoomLevel: level }),
        setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),
        setSidebarExpanded: (open: boolean) => set({ sidebarExpanded: open }),
        setSidebarPreferredWidth: (newValue: number) => set({ sidebarPreferredWidth: newValue }),
        setNewModalOpen: (open: boolean, newModalOptions?: NewModalOptions) => {
          return set((state) => {
            if (state.isNewModalOpen === false && open) {
              logTrackerEvent({
                kind: TrackerEventKind.OPEN_NEW_FDOC_MODAL,
                payload: {
                  type: newModalOptions?.type ?? null,
                  sharedFromMobile: !!newModalOptions?.shared,
                },
              });
            }
            return {
              isNewModalOpen: open,
              newModalOptions: newModalOptions ?? emptyNewModalOptions,
            };
          });
        },
        visitedResourceHistoryIds: [],
        setExpandedFdocSelector: (selector: string | null) =>
          set({ expandedFdocSelector: selector }),
        setNewModalOptions: (options) => {
          if (typeof options === 'function') {
            set((state) => ({ newModalOptions: options(state.newModalOptions) }));
            return;
          }

          set({ newModalOptions: options });
        },
        setLastFdocUpdate: (timestamp: number) => set({ lastFdocUpdate: timestamp }),
        setIsExperimentSearchOverlayOpen: (newState) =>
          set({ isExperimentSearchOverlayOpen: newState }),
        setMobileKeyboardHeight: (height: number) => set({ mobileKeyboardHeight: height }),
        setMobileKeyboardHeightSupported: (value: boolean) =>
          set({ mobileKeyboardHeightSupported: value }),
        setKeyboardOpen: (open: boolean) => set({ keyboardOpen: open }),
        setExpandedFdocSidebarOpen: (open: boolean) => set({ expandedFdocSidebarOpen: open }),
        setExpandedFdocSidebarTab: (tab: ExtraPanelTab) =>
          set({ expandedFdocSidebarTab: tab, expandedFdocSidebarInitTab: undefined }),

        reset: () => set({ searchQuery: '', filter: 'All' }),
        emojiPickerSkinTone: SkinTones.NEUTRAL,
        setEmojiPickerSkinTone: (skinTone: SkinTones) => set({ emojiPickerSkinTone: skinTone }),
      }),
      {
        name: 'UIStore',
        version: 3.1,
        partialize: (state) => {
          // dont persist searchQuery
          const rest = omit(state, [
            'searchQuery',
            'sidebarOpen',
            'newModalOptions',
            'isNewModalOpen',
            'supportsPassiveEvents',
            'isSearchOverlayOpen',
            'requiredUpdateModalOpen',
            'isExperimentSearchOverlayOpen',
            'expandedFdocSidebarTab',
            'expandedFdocSidebarInitTab',
            'mobileKeyboardHeight',
            'expandedResourcePendingUploads',
            'floatingFooterActive',
            'floatingFooters',
          ]);

          return rest;
        },
        migrate(persistedState: unknown, version) {
          const state = persistedState as UIStore;

          if (version < 3.1) {
            return omit(state, ['sortMode' as keyof UIStore]) as UIStore;
          }

          // make sure to update the sortMode
          return {
            ...state,
          };
        },
      },
    ),
  ),
);

export const useExperimentSearchOverlay = () => {
  const router = useRouter();
  const {
    isExperimentSearchOverlayOpen: isHybridSearchOverlayOpen,
    setIsExperimentSearchOverlayOpen: setIsHybridSearchOverlayOpen,
  } = useUIStore(
    (s) => pick(s, ['isExperimentSearchOverlayOpen', 'setIsExperimentSearchOverlayOpen']),
    shallow,
  );

  return {
    isOverlayOpen: isHybridSearchOverlayOpen,
    openOverlay: useCallback(() => {
      setIsHybridSearchOverlayOpen(true);
      router.push({
        pathname: router.pathname,
        query: { ...router.query, experimentSearch: 'global' },
      });
    }, [router, setIsHybridSearchOverlayOpen]),
    closeOverlay: useCallback(() => {
      const { experimentSearch, ...query } = router.query;
      setIsHybridSearchOverlayOpen(false);

      // we remove hybrid search from history if it's there
      if (experimentSearch) {
        router.replace({ pathname: router.pathname, query });
      }
    }, [router, setIsHybridSearchOverlayOpen]),
  };
};

export default useUIStore;
